import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import FormControlLabel from "@mui/material/FormControlLabel"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import { firstApiErrorMessage } from "../../../common/utils"
import { isActiveSubscriptionStatus } from "../../users/billing"
import { selectProfile, selectSubscription } from "../../users/reducer"
import { useStartLeadCall } from "../calling/useStartLeadCall"
import SendEmailMenu, {
  SEND_REQUEST_TYPE,
} from "../candidate-details/SendEmailMenu"
import {
  SubjectField,
  getEmailEditorHTML,
} from "../email-campaigns/edit-email-mixin"
import { selectCRMSettings } from "../reducer"
import { SETTINGS_TABS, createSettingsTabURL } from "../settings"
import { getSmsSendingPrerequisiteState, useDialogContainer } from "../utils"
import ConfirmConnectEmailDialog, {
  useConnectEmailDialogRequirements,
} from "./ConfirmConnectEmailDialog"
import DialogHeader from "./DialogHeader"
import EmailWYSWIGEditor, {
  useEmailWYSWIGEditorState,
} from "./EmailWYSWIGEditor"
import SelectEmailCampaignDialog from "./SelectEmailCampaignDialog"
import SelectSMSCampaignDialog from "./SelectSMSCampaignDialog"
import {
  SendingEmailTrialFinishedDialogData,
  TrialFinishedRoadblockDialogData,
  useTrialFinishedDialogUtils,
} from "./TrialFinishedRoadblockDialog"
import {
  EMAIL_SIGNATURE_API_URL,
  buildSignaturePreviewHtml,
  savedSignatureHasContent,
} from "./emailSignatureUtils"
import { leadContactPayload, toLeadCallContact } from "./leadContactUtils"

function SendLeadSMSCampaignDialog({ lead, leadSource, open, onClose }) {
  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState("")
  const axios = useAuthorizedAxiosClient()

  useEffect(() => {
    if (open) {
      setSendError("")
    }
  }, [open])

  function handleSendButtonClicked(selectedCampaign) {
    setIsSending(true)
    setSendError("")
    axios
      .post(
        `/workflows/api/sms/send-campaign/${selectedCampaign.id}/lead/`,
        leadContactPayload(lead, leadSource)
      )
      .then(() => onClose(true))
      .catch((err) => {
        setSendError(
          firstApiErrorMessage(
            err?.response?.data,
            "Could not send SMS. Please try again."
          )
        )
      })
      .finally(() => setIsSending(false))
  }

  return (
    <SelectSMSCampaignDialog
      open={open}
      onClose={onClose}
      campaignTextPropName="text"
      errorMessage={sendError}
      primaryButton={{
        text: "Send",
        onClick: handleSendButtonClicked,
        isLoading: isSending,
      }}
      campaignsAPIURL="/workflows/api/sms/select-campaign/"
    />
  )
}

function SendLeadEmailDirectlyDialog({ lead, leadSource, open, onClose }) {
  const [subject, setSubject] = useState("")
  const [includeSignature, setIncludeSignature] = useState(false)
  const [signaturePreviewHtml, setSignaturePreviewHtml] = useState("")
  const {
    editorReady,
    setEditorReady,
    insertLinkDialogOpen,
    setInsertLinkDialogOpen,
    editorRef,
  } = useEmailWYSWIGEditorState(open)
  const [isSending, setIsSending] = useState(false)
  const Container = useDialogContainer()
  const axios = useAuthorizedAxiosClient()

  useEffect(() => {
    if (!open) {
      setSubject("")
      setIncludeSignature(false)
      setSignaturePreviewHtml("")
      return
    }

    axios
      .get(EMAIL_SIGNATURE_API_URL)
      .then(({ data }) => {
        setSignaturePreviewHtml(buildSignaturePreviewHtml(data))
        setIncludeSignature(savedSignatureHasContent(data))
      })
      .catch(() => {
        setSignaturePreviewHtml("")
        setIncludeSignature(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function handleSendButtonClicked() {
    setIsSending(true)
    axios
      .post("/workflows/api/email/direct/lead/", {
        ...leadContactPayload(lead, leadSource),
        subject,
        email_template: getEmailEditorHTML(editorRef),
        include_signature: includeSignature,
      })
      .then(({ data }) => onClose(data))
      .finally(() => setIsSending(false))
  }

  return (
    <Container open={open} onClose={() => onClose()} anchor="right">
      <DialogHeader
        title={`Email ${lead?.name || "lead"}`}
        onClose={() => onClose()}
      />
      <DialogContent>
        <Column sx={{ gap: 2 }}>
          <SubjectField subject={subject} setSubject={setSubject} />
          <EmailWYSWIGEditor
            editorReady={editorReady}
            setEditorReady={setEditorReady}
            insertLinkDialogOpen={insertLinkDialogOpen}
            setInsertLinkDialogOpen={setInsertLinkDialogOpen}
            editorRef={editorRef}
          />
          {signaturePreviewHtml ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeSignature}
                  onChange={(e) => setIncludeSignature(e.target.checked)}
                />
              }
              label="Include email signature"
            />
          ) : null}
          {includeSignature && signaturePreviewHtml ? (
            <Typography
              variant="caption"
              color="text.secondary"
              dangerouslySetInnerHTML={{ __html: signaturePreviewHtml }}
            />
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={() => onClose()}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isSending}
          onClick={handleSendButtonClicked}
        >
          Send
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}

function SendLeadEmailCampaignDialog({ lead, leadSource, open, onClose }) {
  const [isSending, setIsSending] = useState(false)
  const axios = useAuthorizedAxiosClient()
  const navigate = useNavigate()

  function handleSendButtonClicked(selectedCampaign, emailBody) {
    setIsSending(true)
    axios
      .post(`/workflows/api/email/send-campaign/${selectedCampaign.id}/lead/`, {
        ...leadContactPayload(lead, leadSource),
        email_body: emailBody,
      })
      .then(({ data }) => onClose(data))
      .finally(() => setIsSending(false))
  }

  return (
    <SelectEmailCampaignDialog
      open={open}
      onClose={() => onClose()}
      bodyPropName="html_body"
      editable
      onCreateLinkClicked={() => navigate("/campaigns/email/create")}
      primaryButton={{
        text: "Send",
        isLoading: isSending,
        onClick: handleSendButtonClicked,
      }}
      campaignsAPIURL="/workflows/api/email/select-campaign/"
    />
  )
}

export function LeadOutreachDialogs({ actionsState }) {
  const {
    actionLead,
    leadSource,
    sendEmailCampaignDialogOpen,
    setSendEmailCampaignDialogOpen,
    sendEmailDirectlyDialogOpen,
    setSendEmailDirectlyDialogOpen,
    sendSMSCampaignDialogOpen,
    setSendSMSCampaignDialogOpen,
    emailMenuRef,
    confirmConnectEmailDialogOpen,
    handleEmailMenuClosed,
    handleConfirmConnectGmailDialogClosed,
    onOutreachComplete,
  } = actionsState

  function closeOutreachDialog(setter) {
    return (sent) => {
      setter(false)
      if (sent) {
        onOutreachComplete?.()
      }
    }
  }

  return (
    <>
      <SendLeadEmailCampaignDialog
        open={sendEmailCampaignDialogOpen}
        onClose={closeOutreachDialog(setSendEmailCampaignDialogOpen)}
        lead={actionLead}
        leadSource={leadSource}
      />
      <SendLeadEmailDirectlyDialog
        open={sendEmailDirectlyDialogOpen}
        onClose={closeOutreachDialog(setSendEmailDirectlyDialogOpen)}
        lead={actionLead}
        leadSource={leadSource}
      />
      <SendLeadSMSCampaignDialog
        open={sendSMSCampaignDialogOpen}
        onClose={closeOutreachDialog(setSendSMSCampaignDialogOpen)}
        lead={actionLead}
        leadSource={leadSource}
      />
      <ConfirmConnectEmailDialog
        open={confirmConnectEmailDialogOpen}
        onClose={handleConfirmConnectGmailDialogClosed}
      />
      <SendEmailMenu
        anchorElem={emailMenuRef}
        onClose={handleEmailMenuClosed}
      />
    </>
  )
}

export function useLeadOutreachActions(
  openSnackbar,
  leadSource = "mls",
  onOutreachComplete
) {
  const [actionLead, setActionLead] = useState(null)
  const [sendEmailCampaignDialogOpen, setSendEmailCampaignDialogOpen] =
    useState(false)
  const [sendEmailDirectlyDialogOpen, setSendEmailDirectlyDialogOpen] =
    useState(false)
  const [sendSMSCampaignDialogOpen, setSendSMSCampaignDialogOpen] =
    useState(false)
  const [emailMenuRef, setEmailMenuRef] = useState(null)

  const subscription = useSelector(selectSubscription)
  const profile = useSelector(selectProfile)
  const crmSettings = useSelector(selectCRMSettings)
  const navigate = useNavigate()
  const { runOnlyIfSubscribed } = useTrialFinishedDialogUtils()
  const { requestCall } = useStartLeadCall(openSnackbar)

  const {
    confirmConnectEmailDialogOpen,
    openConfirmConnectEmailDialog,
    handleConfirmConnectGmailDialogClosed,
    emailAuthorized,
  } = useConnectEmailDialogRequirements(() => {})

  function requirePaidPlan(featureLabel) {
    if (!isActiveSubscriptionStatus(subscription?.status)) {
      openSnackbar?.(
        `Subscribe to a paid plan to ${featureLabel} from My Leads.`
      )
      return false
    }
    return true
  }

  function handleEmailButtonClicked(e, lead) {
    e.stopPropagation()
    if (!requirePaidPlan("send email")) {
      return
    }
    setActionLead(lead)
    runOnlyIfSubscribed(
      () => setEmailMenuRef(e.currentTarget),
      SendingEmailTrialFinishedDialogData
    )
  }

  function handleEmailMenuClosed(result) {
    if (emailAuthorized) {
      switch (result) {
        case SEND_REQUEST_TYPE.campaign:
          setSendEmailCampaignDialogOpen(true)
          break
        case SEND_REQUEST_TYPE.direct:
          setSendEmailDirectlyDialogOpen(true)
          break
        default:
      }
    } else if (result) {
      openConfirmConnectEmailDialog()
    }
    setEmailMenuRef(null)
  }

  function handleSMSButtonClicked(e, lead) {
    e.stopPropagation()
    if (!requirePaidPlan("send SMS")) {
      return
    }
    setActionLead(lead)
    runOnlyIfSubscribed(() => {
      const smsPrereq = getSmsSendingPrerequisiteState(crmSettings, profile)
      if (!smsPrereq.ready) {
        navigate(
          createSettingsTabURL(SETTINGS_TABS.phoneSms, {
            focus:
              smsPrereq.reason === "concierge_no_phone" ? undefined : "sms",
          })
        )
        return
      }
      setSendSMSCampaignDialogOpen(true)
    }, new TrialFinishedRoadblockDialogData("Sending SMS", "Subscribe to gain back the ability to send SMS to your leads"))
  }

  function handleCallButtonClicked(e, lead) {
    e.preventDefault()
    e.stopPropagation()
    requestCall(toLeadCallContact(lead, leadSource))
  }

  return {
    actionLead,
    leadSource,
    sendEmailCampaignDialogOpen,
    setSendEmailCampaignDialogOpen,
    sendEmailDirectlyDialogOpen,
    setSendEmailDirectlyDialogOpen,
    sendSMSCampaignDialogOpen,
    setSendSMSCampaignDialogOpen,
    emailMenuRef,
    confirmConnectEmailDialogOpen,
    handleEmailButtonClicked,
    handleSMSButtonClicked,
    handleCallButtonClicked,
    handleEmailMenuClosed,
    handleConfirmConnectGmailDialogClosed,
    onOutreachComplete,
  }
}
